import { useQuery } from '@tanstack/react-query';
import { TRequestHandlerError } from '@studio-toolkit/request-handler';

import { useQuantum } from 'hooks';
import { apiUrl, requestHandler } from 'utils';
import { useTokenAndHeaders } from 'hooks/useHeaders';

export interface IWeeklyPrintCount {
  week: string;
  success: number;
  failed: number;
  count?: number;
}

export interface IDailyPrintsByWeek {
  week: string;
  dateWisePrints: Record<string, number>;
}

export interface IDailyPrintCount {
  date: string;
  success: number;
  failed: number;
}

export interface IMonthlyPrintCount {
  month: string;
  success: number;
  failed: number;
}

export interface IPrintVolumeByHub {
  hub: string;
  success: number;
  failed: number;
  total: number;
}

export interface ITopOrderType {
  orderType: string;
  count: number;
}

export interface ITopEquipmentType {
  equipmentType: string;
  count: number;
}

export interface ILineOfBusiness {
  lineOfBusiness: string;
  count: number;
}

export interface IHubRanking {
  rank: number;
  hub: string;
  count: number;
}

export interface IAnalyticsData {
  summary: {
    totalPrintVolume: number;
    totalOrdersPrinted: number;
    podSavingsToDate: number;
    successCount?: number;
    failedCount?: number;
  };
  geography?: {
    hub: Array<{
      hub: string;
      count: number;
    }>;
  };
  printVolumeByHub?: IPrintVolumeByHub[];
  topOrderTypes?: ITopOrderType[];
  topEquipmentTypes?: ITopEquipmentType[];
  lob?: ILineOfBusiness[];
  printCount: {
    daily?: IDailyPrintCount[];
    weekly: IWeeklyPrintCount[];
    monthly?: IMonthlyPrintCount[];
    dailyByWeek?: IDailyPrintsByWeek[];
  };
  hubRanking?: IHubRanking[];
}

export interface IAnalyticsApiResponse {
  status: number;
  message: string;
  data: IAnalyticsData;
}

const useFetchAnalytics = (collateralId?: string) => {
  const { trackApiCall } = useQuantum();

  const url = apiUrl({
    endpoint: `analytics/dashboard`,
    apiEnv: import.meta.env.VITE_APP_ENV,
  });

  const { headers } = useTokenAndHeaders();

  return useQuery<IAnalyticsData | null, TRequestHandlerError>({
    queryKey: ['fetchAnalytics', collateralId],
    queryFn: async () => {
      const response = await requestHandler<IAnalyticsApiResponse>({
        requestConfig: {
          method: 'GET',
          url,
          headers,
        },
        responseHandlers: {
          200: (res) => res,
        },
        errorHandlers: {
          500: (error) => {
            console.log(error);
            throw new Error('Failed to fetch analytics');
          },
        },
        quantumHandler: trackApiCall,
      });

      if (!response || response.status !== 200 || !response.data) {
        throw new Error('Analytics fetch failed');
      }

      return response.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
  });
};

export default useFetchAnalytics;
